/**
 * What the admin's date-time pickers may offer: only times inside the tenant's opening hours
 * (DECISIONS.md D33), on the tenant's slot grid (`timeSlotDuration`). The grid counts from each
 * day's opening, as the free-slot generator does, so a day opening at 12:15 on a 30-minute grid
 * offers 12:15 and 12:45.
 *
 * A start may be anywhere from opening up to, but not at, closing; an end anywhere after opening
 * up to closing itself.
 */

/** dayjs numbers the weekdays as `Date.getDay()` does. */
const WEEKDAYS = ['SUNDAY', 'MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY'];

const toMinutes = (time) => {
  const [hour, minute] = time.split(':').map(Number);
  return hour * 60 + minute;
};

const minuteOfDay = (value) => value.hour() * 60 + value.minute();

/** The day's opening and closing in minutes, or null on a day the tenant is closed. */
export const hoursOn = (openingHours, day) => {
  const entry = openingHours.find((hours) => hours.dayOfWeek === WEEKDAYS[day.day()]);
  return entry ? { open: toMinutes(entry.openingTime), close: toMinutes(entry.closingTime) } : null;
};

const isInRange = (minute, hours, edge) =>
  edge === 'start'
    ? minute >= hours.open && minute < hours.close
    : minute > hours.open && minute <= hours.close;

const isOnGrid = (minute, hours, step) => (minute - hours.open) % step === 0;

const isAllowed = (minute, hours, step, edge) => isInRange(minute, hours, edge) && isOnGrid(minute, hours, step);

/** Is this date and time one the picker for `edge` ('start' or 'end') offers? */
export const isAllowedTime = (value, openingHours, step, edge) => {
  const hours = hoursOn(openingHours, value);
  return Boolean(hours) && isAllowed(minuteOfDay(value), hours, step, edge);
};

/** For the picker's `shouldDisableDate`: a day the tenant is closed has nothing to pick. */
export const isClosedDay = (openingHours) => (day) => !hoursOn(openingHours, day);

/**
 * For the picker's `shouldDisableTime(value, view)`. An hour is offered when at least one of its
 * minutes is; a minute when it is inside the hours and on the grid.
 */
export const disabledTimeFor = (openingHours, step, edge) => (value, view) => {
  const hours = hoursOn(openingHours, value);
  if (!hours) return true;

  if (view === 'hours') {
    for (let minute = 0; minute < 60; minute += 1) {
      if (isAllowed(value.hour() * 60 + minute, hours, step, edge)) return false;
    }
    return true;
  }

  if (view === 'minutes') {
    return !isAllowed(minuteOfDay(value), hours, step, edge);
  }

  return false;
};

/** The first start still ahead of `now`: the next grid time of the next open day. */
export const firstAllowedStart = (openingHours, step, now) => {
  for (let offset = 0; offset < 14; offset += 1) {
    const day = now.add(offset, 'day').startOf('day');
    const hours = hoursOn(openingHours, day);
    if (!hours) continue;

    let minute = hours.open;
    if (offset === 0) {
      const nowMinute = minuteOfDay(now);
      while (minute <= nowMinute) minute += step;
    }

    if (minute < hours.close) return day.add(minute, 'minute');
  }

  return now.add(1, 'hour').startOf('hour');
};

/** About an hour after `start`, on the grid, and no later than that day's closing. */
export const defaultEndFor = (start, openingHours, step) => {
  const hours = hoursOn(openingHours, start);
  const target = minuteOfDay(start) + Math.ceil(60 / step) * step;
  return start.startOf('day').add(hours ? Math.min(target, hours.close) : target, 'minute');
};
